import * as React from 'react';
import { DefaultPalette, AnimationClassNames } from 'office-ui-fabric-react/lib/Styling';
import { IconButton, initializeIcons, Stack, IStackItemStyles, IStackStyles } from 'office-ui-fabric-react';

initializeIcons();

// AccordionGroup manages which Accordion is open
export const AccordionGroup: React.FC<{
    children: React.ReactElement<IAccordionProps>[];
    activeIndex?: number;
    onActiveIndexChange?: (index: number) => void;
}> = ({ children, activeIndex, onActiveIndexChange }) => {
    const [openIndex, setOpenIndex] = React.useState(0);
    const isControlled = typeof activeIndex === 'number';
    const currentIndex = isControlled ? (activeIndex as number) : openIndex;
    const setIndex = (idx: number) => {
        if (isControlled) {
            onActiveIndexChange && onActiveIndexChange(idx);
        } else {
            setOpenIndex(idx);
        }
    };

    // Clone children and inject isOpen and onHeaderClick
    return (
        <>
            {React.Children.map(children, (child, idx) =>
                React.cloneElement(child, {
                    isOpen: currentIndex === idx,
                    onHeaderClick: () => setIndex(idx)
                })
            )}
        </>
    );
};

export class Accordion extends React.Component<IAccordionProps> {
    private accordionHeader = (isOpen: boolean): IStackItemStyles => ({
        root: {
            background: isOpen ? DefaultPalette.themePrimary : DefaultPalette.neutralLighter,
            color: isOpen ? DefaultPalette.white : DefaultPalette.neutralPrimary,
            padding: 5,
            cursor: 'pointer'
        }
    });
    private accordion: IStackStyles = {
        root: {
            borderStyle: 'solid',
            borderWidth: 1,
            borderColor: DefaultPalette.neutralTertiary
        }
    };
    private accordionConent: IStackStyles = {
        root: {
            padding: 10
        }
    };

    public render(): React.ReactElement<IAccordionProps> {
        const { isOpen, header, children, onHeaderClick } = this.props;
        return (
            <Stack horizontal={false} styles={this.accordion}>
                <Stack.Item styles={this.accordionHeader(!!isOpen)}>
                    <Stack horizontal={true} onClick={onHeaderClick}>
                        <Stack.Item>
                            <IconButton
                                iconProps={{
                                    iconName: isOpen ? 'ChevronDown' : 'ChevronRight'
                                }}
                                ariaLabel={isOpen ? 'Collapse section' : 'Expand section'}
                                styles={{
                                    root: {
                                        color: isOpen ? DefaultPalette.white : DefaultPalette.neutralPrimary
                                    }
                                }}
                            />
                        </Stack.Item>
                        <Stack.Item
                            align='center'
                            styles={{
                                root: {
                                    color: isOpen ? DefaultPalette.white : DefaultPalette.neutralPrimary,
                                    fontWeight: isOpen ? 600 : 400
                                }
                            }}
                        >
                            {header}
                        </Stack.Item>
                    </Stack>
                </Stack.Item>
                {isOpen && (
                    <Stack.Item className={AnimationClassNames.slideDownIn20} styles={this.accordionConent}>
                        {children}
                    </Stack.Item>
                )}
            </Stack>
        );
    }
}

interface IAccordionProps {
    header: string;
    isOpen?: boolean;
    onHeaderClick?: () => void;
    children?: React.ReactNode;
}

// Usage Example (in your parent component):
/*
<AccordionGroup>
  <Accordion header="Section 1">Content 1</Accordion>
  <Accordion header="Section 2">Content 2</Accordion>
  <Accordion header="Section 3">Content 3</Accordion>
</AccordionGroup>
*/
